import { CommonModule } from '@angular/common';
import { Component, computed, inject, Inject, Injector, input, signal, TemplateRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { TranslateForCrmObjectPipe, TranslateForCrmObjectService, TranslationModule } from '../../../i18n';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { CommonAssociationCardContentComponent, TagsComponent } from '../association-card';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  Observable,
  of,
  shareReplay,
  withLatestFrom,
  zip,
} from 'rxjs';
import {
  CrmDependencies,
  CrmInjectionToken,
  CrmMultiLocationDependencies,
  CrmMultilocationInjectionToken,
  ObjectType,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
  tableFiltersInjectionTokenGenerator,
} from '../../../tokens-and-interfaces';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { Association, CrmAssociationService, CrmObjectService } from '../../../shared-services';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AssociationCardData, CardAction, CreateCardData } from './tokens-and-interfaces';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatDialog } from '@angular/material/dialog';
import {
  EditAssociationTagsData,
  EditAssociationTagsDialogComponent,
  EditAssociationTagsResponse,
} from './edit-association-tags-dialog/edit-association-tags-dialog.component';
import { GalaxyFilterChipInjectionToken } from '@vendasta/galaxy/filter/chips';
import {
  ListObjectTypeDialogData,
  ListObjectTypeDialogResult,
  SelectObjectTypeModalComponent,
} from '../../select-object-type-modal/select-object-type-modal.component';
import { getAssociationType } from '../../../shared-services/association.utils';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import {
  SelectPrimaryAssociationDialogData,
  SelectPrimaryAssociationDialogResult,
  SelectPrimaryAssociationModalComponent,
} from '../select-primary-association-modal/select-primary-association-modal.component';
import {
  createCrmObjectModalProviders,
  CrmCreateCrmObjectModalComponent,
  CrmCreateCrmObjectModalResult,
} from '../../create-crm-object-modal';
import {
  ConfirmExistingPrimaryAssociationDialogData,
  ConfirmExistingPrimaryAssociationModalComponent,
} from '../confirm-existing-primary-association-modal/confirm-existing-primary-association-modal.component';
import { CustomObjectType } from '@vendasta/crm';

enum updateTypes {
  Removed = 'Removed',
  Added = 'Added',
}

interface CreateAssociationsResponse {
  error: boolean;
  idsCreated: string[];
  associatedIdToObjectId: Map<string, string>;
}

@Component({
  selector: 'crm-object-association-panel',
  templateUrl: './crm-object-association-panel.component.html',
  styleUrls: ['../../../shared-styles/association-panel-shared.scss', './crm-object-association-panel.component.scss'],
  imports: [
    CommonModule,
    MatCardModule,
    GalaxyAvatarModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    TranslationModule,
    RouterLink,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    CommonAssociationCardContentComponent,
    TagsComponent,
    TranslateForCrmObjectPipe,
    GalaxyLoadingSpinnerModule,
  ],
  providers: [],
})
export class CrmObjectAssociationPanelComponent {
  associatedObject = input.required<ObjectType>();
  associatedObjectCustomType = input<CustomObjectType | null>(null);
  associatedObjectCustomTypeID$ = toObservable<CustomObjectType | null>(this.associatedObjectCustomType).pipe(
    map((customType) => customType?.customObjectTypeId || ''),
  );
  private readonly associatedObject$ = toObservable(this.associatedObject);
  id = input.required<string>();
  private readonly id$ = toObservable(this.id);
  name = input('');
  private readonly name$ = toObservable(this.name);
  objectType = input.required<ObjectType>();
  private readonly objectType$ = toObservable(this.objectType);
  objectSubtype = input<string>('');
  private readonly objectSubtype$ = toObservable(this.objectSubtype);
  customHeaderContent = input<TemplateRef<unknown>>();
  customCardContent = input<TemplateRef<unknown>>();
  createCardData = input.required<CreateCardData>();
  customActions = input<CardAction[]>([]);
  requiresPrimaryAssociation = input(false);
  confirmExistingPrimaryAssociations = input(false);
  isMultiLocation = toSignal(this.multiLocationConfig.isMultiLocation$ ?? of(false));
  showAssociationLink = computed(() => !this.isMultiLocation());

  private readonly associationType = computed(() => {
    const fromObject = this.objectType();
    const toObject = this.associatedObject();
    return getAssociationType(fromObject, toObject);
  });

  private readonly injector = inject(Injector);
  private readonly tokenInjector = computed(() => {
    const associatedObject = this.associatedObject();
    const associatedObjectSubtype = this.associatedObjectCustomType()?.customObjectTypeId;
    return Injector.create({
      providers: [
        {
          provide: PageAnalyticsInjectionToken,
          useFactory: pageAnalyticsInjectionTokenGenerator(associatedObject, associatedObjectSubtype),
        },
        {
          provide: GalaxyFilterChipInjectionToken,
          useFactory: tableFiltersInjectionTokenGenerator(associatedObject, undefined, false),
        },
      ],
      parent: this.injector,
    });
  });

  private readonly associationsWithUpdatedTags = signal<Map<string, string[]>>(new Map<string, string[]>());
  private readonly associationsWithUpdatedTags$ = toObservable(this.associationsWithUpdatedTags);

  private readonly updatedAssociations = signal<Map<string, updateTypes>>(new Map<string, updateTypes>());
  private readonly updatedAssociations$ = toObservable(this.updatedAssociations);

  readonly primaryAssociationId$: Observable<string>;
  private readonly hasPrimaryAssociation$: Observable<boolean>;
  private readonly associations$: Observable<Association[]>;
  protected readonly cards$: Observable<AssociationCardData[]>;

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(CrmMultilocationInjectionToken) private readonly multiLocationConfig: CrmMultiLocationDependencies,
    private readonly associationService: CrmAssociationService,
    private readonly confirmationModalService: OpenConfirmationModalService,
    private readonly objectService: CrmObjectService,
    private readonly translateService: TranslateService,
    private readonly snackService: SnackbarService,
    private readonly dialog: MatDialog,
    private readonly translateForCrmObjectService: TranslateForCrmObjectService,
  ) {
    const routePrefix$ = this.config.routePrefix$;
    // this is needed because in PCC
    // the card is never destroyed
    this.id$.pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe(() => {
      this.updatedAssociations.set(new Map<string, updateTypes>());
    });
    const initialAssociations$ = combineLatest([
      this.id$,
      this.objectType$,
      this.associatedObject$,
      this.objectSubtype$,
      this.associatedObjectCustomTypeID$,
    ]).pipe(
      switchMap(([id, objectType, associatedObject, objectSubtype, associatedObjectSubtype]) => {
        return this.associationService.getAssociationsForObject$(
          id,
          objectType,
          associatedObject,
          1000,
          [],
          objectSubtype,
          associatedObjectSubtype,
        );
      }),
      map((associations) => {
        return associations
          .map((association) => association.associationId ?? '')
          .filter((association) => !!association);
      }),
      withLatestFrom(this.objectType$, this.associatedObject$, this.objectSubtype$, this.associatedObjectCustomTypeID$),
      switchMap(([ids, objectType, associatedObject, objectSubtype, associatedObjectSubtype]) =>
        this.associationService.getAssociationsFromAssociationIds$(
          ids,
          objectType,
          associatedObject,
          objectSubtype,
          associatedObjectSubtype,
        ),
      ),
      map((associations) => {
        return associations.filter((association) => association?.associatedObject);
      }),
      shareReplay(1),
    );
    const addedAssociations$ = this.updatedAssociations$.pipe(
      map((updatedMap) => {
        const addedIds: string[] = [];
        updatedMap.forEach((value, key) => {
          if (value === updateTypes.Added) {
            addedIds.push(key);
          }
        });
        return addedIds;
      }),
      withLatestFrom(this.objectType$, this.associatedObject$, this.objectSubtype$, this.associatedObjectCustomTypeID$),
      switchMap(([addedIds, objectType, associatedObject, objectSubtype, associatedObjectSubtype]) =>
        this.associationService.getAssociationsFromAssociationIds$(
          addedIds,
          objectType,
          associatedObject,
          objectSubtype,
          associatedObjectSubtype,
        ),
      ),
      startWith([] as Association[]),
      shareReplay(1),
    );

    this.associations$ = combineLatest([
      initialAssociations$,
      this.updatedAssociations$,
      this.associationsWithUpdatedTags$,
      addedAssociations$,
    ]).pipe(
      map(([initialAssociations, updatedAssociations, updatedTags, addedAssociations]) => {
        const withRemoved = initialAssociations.filter(
          (association) =>
            association?.associatedObject?.crmObjectId &&
            updatedAssociations.get(association.associationId) !== updateTypes.Removed,
        );
        const withRemovedAndAdded = [...addedAssociations, ...withRemoved];
        return this.addTagUpdatesToAssociations(withRemovedAndAdded, updatedTags);
      }),
      shareReplay(1),
    );

    this.primaryAssociationId$ = this.associations$.pipe(
      map((associations) => {
        const primaryAssociations = associations.map((association) =>
          association?.tags?.includes('Primary') ? association?.associationId : '',
        );
        return primaryAssociations.find((a) => !!a) ?? '';
      }),
    );
    this.hasPrimaryAssociation$ = this.primaryAssociationId$.pipe(
      map((primaryAssociationId) => !!primaryAssociationId),
    );

    this.cards$ = combineLatest([this.associations$, routePrefix$.pipe(startWith(''))]).pipe(
      map(([associations, routePrefix]) => {
        const cards = this.createCardData()(associations, routePrefix);
        return cards.sort(function (a, b) {
          const aName = a.name?.toLowerCase() ?? '';
          const bName = b.name?.toLowerCase() ?? '';
          const aTags = a.tags?.length ?? 0;
          const bTags = b.tags?.length ?? 0;
          if (aTags === bTags) {
            return aName < bName ? -1 : aName > bName ? 1 : 0;
          }
          return aTags > bTags ? -1 : aTags < bTags ? 1 : 0;
        });
      }),

      shareReplay(1),
    );
  }

  protected async addAssociation() {
    const selectAssociationModalResult = await firstValueFrom(this.openSelectAssociationsModal(false));
    let objectIdsToAdd = selectAssociationModalResult?.selectedRows?.map((crmObject) => crmObject.id) ?? [];
    let clickedCreate = selectAssociationModalResult?.clickedCreate ?? false;

    while (clickedCreate) {
      const createModalResult = await firstValueFrom(this.openCreateCrmObjectModal());
      if (createModalResult?.crmObject?.crmObjectId) {
        objectIdsToAdd = [createModalResult.crmObject.crmObjectId];
        clickedCreate = false;
      } else {
        const selectModalResult = await firstValueFrom(this.openSelectAssociationsModal(false, objectIdsToAdd));
        objectIdsToAdd = selectModalResult?.selectedRows?.map((crmObject) => crmObject.id) ?? [];
        clickedCreate = selectModalResult?.clickedCreate ?? false;
      }
    }

    if (objectIdsToAdd?.length !== 0) {
      const hasPrimary = await firstValueFrom(this.hasPrimaryAssociation$);
      if (this.requiresPrimaryAssociation() && !hasPrimary) {
        const dialogResult = await firstValueFrom(this.openSelectPrimaryAssociationModal(objectIdsToAdd));
        if (dialogResult.continue) {
          const tagMap = dialogResult.tagMap;
          await this.addAssociationsWithTags(objectIdsToAdd, tagMap);
        }
      } else {
        if (this.confirmExistingPrimaryAssociations()) {
          const objectIdsWithPrimaryAssociation =
            await this.getObjectIdsWithExistingPrimaryAssociations(objectIdsToAdd);
          if (objectIdsWithPrimaryAssociation?.length > 0) {
            const dialogResult = await firstValueFrom(
              this.openConfirmExistingPrimaryAssociationModal(objectIdsWithPrimaryAssociation, objectIdsToAdd),
            );
            if (dialogResult.continue) {
              const tagMap = dialogResult.tagMap;
              await this.addAssociationsWithTags(objectIdsToAdd, tagMap);
            }
          } else {
            const tagMap = new Map<string, string[]>(objectIdsToAdd.map((o) => [o, ['Primary']]));
            await this.addAssociationsWithTags(objectIdsToAdd, tagMap);
          }
          return;
        }
        await this.addAssociationsWithTags(objectIdsToAdd);
      }
    }
  }

  private async getObjectIdsWithExistingPrimaryAssociations(objectIdsToAdd: string[]) {
    const partialObservables$ = objectIdsToAdd
      .map((crmObjectId) => {
        if (!crmObjectId) {
          return undefined;
        }
        return this.associationService
          .getAssociationsWithAssociatedObjectForObject$(
            crmObjectId,
            this.associatedObject(),
            this.objectType(),
            1,
            ['Primary'],
            this.objectSubtype(),
            this.associatedObjectCustomType()?.customObjectTypeId,
          )
          .pipe(
            map((results) => {
              if (results?.length > 0) {
                return crmObjectId ?? '';
              }
              return '';
            }),
          );
      })
      .filter((v) => !!v);
    const objectsWithPrimaryAssociation$ = zip(...partialObservables$).pipe(
      map((results) => results.flat().filter((v) => !!v)),
    );
    return await firstValueFrom(objectsWithPrimaryAssociation$);
  }

  private async addAssociationsWithTags(objectIdsToAdd: string[], tagMap?: Map<string, string[]>) {
    const resp = await this.createObjectAssociations(objectIdsToAdd, tagMap);
    if (resp.idsCreated.length > 0) {
      this.updatedAssociations.update((val) => {
        const updatedMap = new Map(val); //Shallow copy required to trigger update
        resp.idsCreated.forEach((added) => updatedMap.set(added, updateTypes.Added));
        return updatedMap;
      });
      if (tagMap) {
        this.associationsWithUpdatedTags.update((prev) => {
          const updatedMap = new Map(prev); //Shallow copy to trigger update
          resp.idsCreated.forEach((added) => {
            const objectId = resp.associatedIdToObjectId.get(added);
            const tags = tagMap.get(objectId ?? '') ?? [];
            updatedMap.set(added, tags);
          });
          return updatedMap;
        });
      }
    }
    this.openAddAssociationResultSnack(resp.error, resp.idsCreated.length, objectIdsToAdd.length);
  }

  private openAddAssociationResultSnack(errorSnack: boolean, successfulIds: number, totalIds: number) {
    if (totalIds === 1) {
      if (successfulIds !== 1 || errorSnack) {
        this.snackService.openErrorSnack('ASSOCIATIONS.SUCCESS.SINGLE_FAILURE');
      } else {
        this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.GENERIC');
      }
    } else if (errorSnack) {
      this.snackService.openErrorSnack('ASSOCIATIONS.SUCCESS.PARTIAL_SUCCESS', {
        interpolateTranslateParams: {
          createdCount: String(successfulIds),
          totalCount: String(totalIds),
        },
      });
    } else {
      this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.PARTIAL_SUCCESS', {
        interpolateTranslateParams: {
          createdCount: String(successfulIds),
          totalCount: String(totalIds),
        },
      });
    }
  }

  private openConfirmExistingPrimaryAssociationModal(
    ids: string[],
    allIds: string[],
  ): Observable<SelectPrimaryAssociationDialogResult> {
    return this.objectService.getMultiObject(this.associatedObject(), allIds).pipe(
      map((resp) => resp.crmObjects ?? []),
      switchMap((objects) => {
        return this.dialog
          .open(ConfirmExistingPrimaryAssociationModalComponent, {
            data: {
              existingPrimaryObjectIds: ids,
              selectedObjects: objects,
              associationType: this.associationType(),
              objectType: this.associatedObject(),
            } as ConfirmExistingPrimaryAssociationDialogData,
            injector: this.tokenInjector(),
          })
          .afterClosed();
      }),
    );
  }

  private openSelectPrimaryAssociationModal(ids: string[]): Observable<SelectPrimaryAssociationDialogResult> {
    return this.objectService.getMultiObject(this.associatedObject(), ids).pipe(
      map((resp) => resp.crmObjects ?? []),
      switchMap((objects) => {
        return this.dialog
          .open(SelectPrimaryAssociationModalComponent, {
            data: {
              selectedObjects: objects,
              associationType: this.associationType(),
              objectType: this.associatedObject(),
            } as SelectPrimaryAssociationDialogData,
            injector: this.tokenInjector(),
          })
          .afterClosed();
      }),
    );
  }

  private openCreateCrmObjectModal(): Observable<CrmCreateCrmObjectModalResult> {
    return this.dialog
      .open(CrmCreateCrmObjectModalComponent, {
        data: {
          objectType: this.associatedObject(),
          objectSubtype: this.associatedObjectCustomType()?.customObjectTypeId,
        },
        injector: createCrmObjectModalProviders(this.associatedObject(), this.injector),
        width: '500px',
      })
      .afterClosed();
  }

  private openSelectAssociationsModal(
    hideCreateButton: boolean,
    preselectedObjectIds?: string[],
  ): Observable<ListObjectTypeDialogResult> {
    return this.associations$.pipe(
      take(1),
      withLatestFrom(
        this.translateForCrmObjectService.getTranslationForCrmObject(
          this.associatedObject(),
          'ASSOCIATION_PANEL.TITLE_ASSOCIATE',
          {
            objectName: this.name(),
          },
        ),
        this.translateForCrmObjectService.getTranslationForCrmObject(
          this.associatedObject(),
          'ASSOCIATION_PANEL.CREATE_BUTTON',
        ),
      ),
      switchMap(([existingAssociations, titleAssociate, createButton]) => {
        return this.dialog
          .open(SelectObjectTypeModalComponent, {
            data: {
              baseColumnIds: [],
              objectType: this.associatedObject(),
              modalTitle: titleAssociate,
              associations: existingAssociations,
              associationType: this.associationType(),
              newSelection: '',
              hideCreateButton: hideCreateButton,
              showActions: false,
              hideActionButton: true,
              preselectedIds: preselectedObjectIds ?? [],
              createButtonText: createButton,
            } as ListObjectTypeDialogData,
            injector: this.tokenInjector(),
          })
          .afterClosed() as Observable<ListObjectTypeDialogResult>;
      }),
    );
  }

  private async createObjectAssociations(
    idsToAdd: string[],
    tagMap?: Map<string, string[]>,
  ): Promise<CreateAssociationsResponse> {
    const updatesToDo = idsToAdd.map((id) => {
      return firstValueFrom(
        this.associationService.createObjectAssociation$(
          this.objectType(),
          this.id(),
          id,
          this.associationType(),
          tagMap?.get(id),
        ),
      );
    });
    const responses = await Promise.all(updatesToDo);
    const associatedIdToObjectId = new Map<string, string>();
    responses.forEach((resp, index) => {
      if (resp) {
        associatedIdToObjectId.set(resp.associationId, idsToAdd[index]);
      }
    });
    const createdIds = responses.filter((resp) => resp !== null).map((resp) => resp.associationId);

    return {
      error: createdIds.length !== responses.length,
      idsCreated: createdIds,
      associatedIdToObjectId: associatedIdToObjectId,
    };
  }

  protected async removeAssociation(cardData: AssociationCardData) {
    const associationId = cardData.associationId;
    if (associationId) {
      const confirmation = await firstValueFrom(
        this.confirmationModalService.openModal({
          type: 'warn',
          title: 'ASSOCIATIONS.REMOVE_DIALOG.TITLE',
          message: this.translateService.instant('ASSOCIATIONS.REMOVE_DIALOG.MESSAGE', {
            fromName: this.name(),
            removeName: cardData.name,
          }),
          confirmButtonText: 'ASSOCIATIONS.REMOVE_DIALOG.CONFIRM',
          cancelButtonText: 'ACTIONS.CANCEL',
        }),
      );
      if (confirmation) {
        this.associationService
          .removeAssociation(associationId)
          .pipe(take(1))
          .subscribe((success) => {
            if (success) {
              this.snackService.openSuccessSnack('ASSOCIATIONS.SUCCESS.REMOVE');
              this.updatedAssociations.update((val) => {
                const updatedMap = new Map(val); //Shallow copy required to trigger update
                updatedMap.set(associationId, updateTypes.Removed);
                return updatedMap;
              });
            } else {
              this.snackService.openErrorSnack('ASSOCIATIONS.ERROR.REMOVE');
            }
          });
      }
    }
  }

  private addTagUpdatesToAssociations(associations: Association[], changes: Map<string, string[]>): Association[] {
    associations.forEach((item) => {
      const hasUpdates = changes.get(item.associationId);
      if (hasUpdates) {
        item.tags = hasUpdates;
      }
    });
    return associations;
  }

  protected async setAsPrimary(cardData: AssociationCardData) {
    const associationId = cardData.associationId;
    const associationName = cardData?.name ?? '';
    if (associationId === '') {
      return;
    }
    const primaryAssociationId = await firstValueFrom(this.primaryAssociationId$);
    const allCards = await firstValueFrom(this.cards$);
    const primaryAssociationName =
      allCards.find((a) => a.associationId === primaryAssociationId)?.name ?? primaryAssociationId;
    if (associationId === primaryAssociationId) {
      return;
    }
    const association$ = this.associationService.getAssociation(associationId).pipe(take(1), shareReplay(1));
    const { tags: associationTags } = await firstValueFrom(association$);
    const existingTags = (associationTags ?? []).map((a) => a?.tagValue ?? '').filter((v) => v !== '');
    const newAssociationTags = ['Primary', ...existingTags];
    const updatePrimaryAssociation$ = this.associationService
      .updateAssociationTags(associationId, newAssociationTags)
      .pipe(
        take(1),
        catchError((_) => {
          return of(false);
        }),
      );
    if (primaryAssociationId === '') {
      const result = await firstValueFrom(updatePrimaryAssociation$);
      if (result) {
        this.snackService.openSuccessSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.SUCCESS');
        this.associationsWithUpdatedTags.update((prev) => {
          const updatedMap = new Map(prev); //Shallow copy to trigger update
          updatedMap.set(associationId, newAssociationTags);
          return updatedMap;
        });
        return;
      }
      this.snackService.openErrorSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.ERROR');
      return;
    }

    const primaryAssociation$ = this.associationService
      .getAssociation(primaryAssociationId)
      .pipe(take(1), shareReplay(1));
    const { tags: primaryTags } = await firstValueFrom(primaryAssociation$);
    const primaryExistingTags = (primaryTags ?? []).map((v) => v.tagValue ?? '').filter((v) => v !== '');
    const newPrimaryTags = primaryExistingTags.filter((t) => t !== 'Primary');
    const removePrimaryFromExisting$ = this.associationService
      .updateAssociationTags(primaryAssociationId, newPrimaryTags)
      .pipe(
        take(1),
        catchError((_) => {
          return of(false);
        }),
      );
    const modalConfirmation$ = combineLatest([
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.associatedObject(),
        'ASSOCIATION_PANEL.CHANGE_PRIMARY_DIALOG.TITLE',
      ),
      this.translateForCrmObjectService.getTranslationForCrmObject(
        this.associatedObject(),
        'ASSOCIATION_PANEL.CHANGE_PRIMARY_DIALOG.MESSAGE',
        { newAssociationName: associationName, previousAssociationName: primaryAssociationName },
      ),
    ]).pipe(
      switchMap(([title, message]) => {
        return this.confirmationModalService.openModal({
          type: 'confirm',
          title: title,
          message: message,
          hideCancel: false,
          confirmButtonText: 'ACTIONS.CONFIRM',
          cancelButtonText: 'ACTIONS.CANCEL',
        });
      }),
    );
    const modalConfirmed = await firstValueFrom(modalConfirmation$);
    if (!modalConfirmed) {
      return;
    }
    const updateWithConfirmation$ = removePrimaryFromExisting$.pipe(
      switchMap((proceed) => {
        if (proceed) {
          return updatePrimaryAssociation$;
        }
        return of(false);
      }),
    );
    const success = await firstValueFrom(updateWithConfirmation$);
    if (success) {
      this.snackService.openSuccessSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.SUCCESS');
      this.associationsWithUpdatedTags.update((prev) => {
        const updatedMap = new Map(prev); //Shallow copy to trigger update
        updatedMap.set(associationId, newAssociationTags);
        updatedMap.set(primaryAssociationId, newPrimaryTags);
        return updatedMap;
      });
    } else {
      this.snackService.openErrorSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.ERROR');
    }
  }

  protected async editTags(cardData: AssociationCardData) {
    const associationId = cardData.associationId;
    if (associationId === '') {
      return;
    }
    const dialogResponse: EditAssociationTagsResponse = await firstValueFrom(
      this.associationService.getAssociation(associationId).pipe(
        switchMap((association) =>
          this.dialog
            .open(EditAssociationTagsDialogComponent, {
              data: {
                association: association,
                associationName: cardData.name,
              } as EditAssociationTagsData,
              width: '500px',
            })
            .afterClosed(),
        ),
      ),
    );
    if (dialogResponse.proceed) {
      this.associationService
        .updateAssociationTags(associationId, dialogResponse.updatedTags)
        .pipe(
          take(1),
          catchError((_) => {
            return of(false);
          }),
        )
        .subscribe((success) => {
          if (success) {
            this.snackService.openSuccessSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.SUCCESS');
            this.associationsWithUpdatedTags.update((prev) => {
              const updatedMap = new Map(prev); //Shallow copy to trigger update
              updatedMap.set(associationId, dialogResponse.updatedTags);
              return updatedMap;
            });
          } else {
            this.snackService.openErrorSnack('ASSOCIATIONS.EDIT_ASSOCIATION_DIALOG.ERROR');
          }
        });
    }
  }
}
